import React from 'react';
import {
  Box,
  InputBase,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import ClearIcon from '@mui/icons-material/Clear';
import PropTypes from 'prop-types';
import useThemeStyles from '../../../hooks/useThemeStyles';
import {
  DEFAULT_PROJECT_FILTERS,
  hasActiveFilters,
  PROJECT_SORT_OPTIONS,
} from '../../../utils/dashboard/filterProjects';

/**
 * Search / type / status filters plus the grid–list view toggle for the
 * dashboard's project listing.
 */
const DashboardToolbar = ({
  filters,
  onFiltersChange,
  typeOptions,
  statusOptions,
  viewMode,
  onViewModeChange,
  sortKey,
  onSortChange,
}) => {
  const { colors, isDark } = useThemeStyles();

  const filtersActive = hasActiveFilters(filters);

  const controlSx = {
    height: 36,
    borderRadius: '8px',
    border: `1px solid ${colors.border.subtle}`,
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.7)',
    color: colors.text.primary,
    fontSize: '0.8125rem',
  };

  // Select popups render in a portal, outside the themed DOM — style the
  // dropdown paper explicitly so it stays legible in dark mode.
  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: colors.background.surface,
        color: colors.text.primary,
        border: `1px solid ${colors.border.primary}`,
        '& .MuiMenuItem-root': {
          fontSize: '0.8125rem',
          color: colors.text.primary,
          '&:hover': { backgroundColor: colors.sidebar.headerHover },
          '&.Mui-selected': {
            backgroundColor: colors.sidebar.headerHover,
            '&:hover': { backgroundColor: colors.sidebar.headerHover },
          },
        },
      },
    },
  };

  const selectSx = {
    ...controlSx,
    minWidth: 130,
    '& .MuiSelect-select': { py: 0.75, px: 1.5 },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiSelect-icon': { color: colors.text.secondary },
  };

  const setFilter = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      {/* Search */}
      <Box
        sx={{
          ...controlSx,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          width: { xs: '100%', sm: 220 },
          '&:focus-within': { borderColor: colors.interactive.primary },
        }}
      >
        <SearchIcon sx={{ fontSize: '1rem', color: colors.text.secondary }} />
        <InputBase
          value={filters.query}
          onChange={(event) => setFilter('query', event.target.value)}
          placeholder="Filter projects…"
          inputProps={{ 'aria-label': 'Filter projects' }}
          sx={{
            flex: 1,
            color: colors.text.primary,
            fontSize: '0.8125rem',
            '& input::placeholder': { color: colors.text.muted, opacity: 1 },
          }}
        />
      </Box>

      {/* Type */}
      <Select
        value={filters.type}
        onChange={(event) => setFilter('type', event.target.value)}
        MenuProps={menuProps}
        sx={selectSx}
        inputProps={{ 'aria-label': 'Filter by project type' }}
      >
        <MenuItem value="all">All types</MenuItem>
        {typeOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>

      {/* Status */}
      <Select
        value={filters.status}
        onChange={(event) => setFilter('status', event.target.value)}
        MenuProps={menuProps}
        sx={selectSx}
        inputProps={{ 'aria-label': 'Filter by status' }}
      >
        <MenuItem value="all">Any status</MenuItem>
        {statusOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>

      {/* Sort */}
      <Select
        value={sortKey}
        onChange={(event) => onSortChange(event.target.value)}
        MenuProps={menuProps}
        sx={selectSx}
        inputProps={{ 'aria-label': 'Sort projects' }}
      >
        {PROJECT_SORT_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>

      {/* Clear (only while something is filtered) */}
      {filtersActive && (
        <Button
          size="small"
          startIcon={<ClearIcon sx={{ fontSize: '0.875rem' }} />}
          onClick={() => onFiltersChange({ ...DEFAULT_PROJECT_FILTERS })}
          sx={{
            height: 36,
            color: colors.text.secondary,
            textTransform: 'none',
            fontSize: '0.8125rem',
            '&:hover': { color: colors.text.primary, backgroundColor: 'transparent' },
          }}
        >
          Clear
        </Button>
      )}

      {/* Grid / list toggle */}
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={(event, next) => {
          if (next) onViewModeChange(next);
        }}
        aria-label="Project view mode"
        sx={{
          height: 36,
          borderRadius: '8px',
          border: `1px solid ${colors.border.subtle}`,
          '& .MuiToggleButton-root': {
            border: 'none',
            px: 1.5,
            color: colors.text.secondary,
            '&.Mui-selected': {
              color: colors.interactive.primary,
              backgroundColor: isDark ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.08)',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(59, 130, 246, 0.18)' : 'rgba(59, 130, 246, 0.12)',
              },
            },
          },
        }}
      >
        <ToggleButton value="grid" aria-label="Grid view">
          <GridViewIcon sx={{ fontSize: '1.125rem' }} />
        </ToggleButton>
        <ToggleButton value="list" aria-label="List view">
          <ViewListIcon sx={{ fontSize: '1.125rem' }} />
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

const optionShape = PropTypes.arrayOf(
  PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })
);

DashboardToolbar.propTypes = {
  filters: PropTypes.shape({
    query: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  typeOptions: optionShape.isRequired,
  statusOptions: optionShape.isRequired,
  viewMode: PropTypes.oneOf(['grid', 'list']).isRequired,
  onViewModeChange: PropTypes.func.isRequired,
  sortKey: PropTypes.string.isRequired,
  onSortChange: PropTypes.func.isRequired,
};

export default DashboardToolbar;
